import type { Canvas } from 'fabric';

import { Circle, Image as FabricImage, Group, Rect, Textbox } from 'fabric';
import { useCallback } from 'react';
import type { ActiveDevice } from '../store/atoms/editorUiAtom';
import type { VariantKey } from '../store/atoms/slideAtom';
import { buildBannerCtaGroup } from '../utils/buildBannerCtaGroup';
import { getDataUrlSizeInBytes } from '../utils/getDataUrlSizeInBytes';

export type SlideElementType =
  | 'text'
  | 'heading'
  | 'image'
  | 'rect'
  | 'circle'
  | 'line'
  | 'tag'
  | 'banner-cta';

export interface UseAddElementParams {
  readonly getActiveCanvas: () => Canvas | null;
  readonly saveCurrentSlide: (key: VariantKey) => void;
  readonly pushSnapshot: (key: string) => void;
  readonly variantKey: VariantKey;
  readonly activeDevice: ActiveDevice;
  readonly alert: (options: {
    alertType: 'success' | 'warning' | 'danger' | 'information';
    title: string;
  }) => void;
}

/**
 * Fábrica de elementos do canvas.
 * Cria e adiciona objetos Fabric.js ao canvas ativo com as dimensões e estilos padrão.
 *
 * @param params - Canvas ativo, funções de persistência e contexto de device/variant
 * @returns `addElement(type, imageDataUrl?)` para inserir qualquer tipo de elemento
 */
export function useAddElement({
  getActiveCanvas,
  saveCurrentSlide,
  pushSnapshot,
  variantKey,
  activeDevice,
  alert,
}: UseAddElementParams) {
  const addElement = useCallback(
    (type: SlideElementType, imageDataUrl?: string) => {
      const canvas = getActiveCanvas();
      if (!canvas) return;

      let obj: Circle | Rect | Textbox | FabricImage | Group | null = null;
      let centerOnCanvas = true;
      const isMobile = activeDevice === 'mobile';

      switch (type) {
        case 'text':
          obj = new Textbox('Digite aqui', {
            width: isMobile ? 140 : 300,
            fontSize: isMobile ? 10 : 16,
            fill: '#ffffff',
            fontFamily: 'Inter, sans-serif',
            data: { id: crypto.randomUUID(), label: 'Texto' },
          });
          break;

        case 'heading':
          obj = new Textbox('TÍTULO', {
            width: isMobile ? 160 : 400,
            fontSize: isMobile ? 18 : 32,
            fontWeight: '700',
            fill: '#ffffff',
            fontFamily: 'Inter, sans-serif',
            charSpacing: isMobile ? 40 : 80,
            data: { id: crypto.randomUUID(), label: 'Título' },
          });
          break;

        case 'rect':
          obj = new Rect({
            width: isMobile ? 100 : 200,
            height: isMobile ? 50 : 100,
            fill: '#3b82f6',
            rx: isMobile ? 5 : 8,
            ry: isMobile ? 5 : 8,
            data: { id: crypto.randomUUID(), label: 'Retângulo' },
          });
          break;

        case 'circle':
          obj = new Circle({
            radius: isMobile ? 30 : 60,
            fill: '#f59e0b',
            data: { id: crypto.randomUUID(), label: 'Círculo' },
          });
          break;

        case 'line':
          obj = new Rect({
            width: isMobile ? 80 : 60,
            height: 2,
            fill: '#38bdf8',
            rx: 1,
            ry: 1,
            data: { id: crypto.randomUUID(), label: 'Linha' },
          });
          break;

        case 'tag': {
          const tagWidth = isMobile ? 80 : 120;
          const tagHeight = isMobile ? 24 : 34;
          const tagRadius = tagHeight * 0.5;
          const tagFontSize = isMobile ? 9 : 13;
          const tagTextWidth = isMobile ? 70 : 110;

          const bg = new Rect({
            left: 0,
            top: 0,
            width: tagWidth,
            height: tagHeight,
            padding: 4,
            fill: '#E4E4E7',
            rx: tagRadius,
            ry: tagRadius,
            originX: 'left',
            originY: 'top',
            lockMovementX: true,
            lockMovementY: true,
            selectable: true,
            evented: true,
            hasControls: true,
            hasBorders: true,
          });

          const label = new Textbox('categoria', {
            left: tagWidth / 2,
            top: tagHeight / 2,
            originX: 'center',
            originY: 'center',
            width: tagTextWidth,
            fontSize: tagFontSize,
            fontWeight: '500',
            fill: '#18181B',
            fontFamily: 'Inter, sans-serif',
            textAlign: 'center',
            lockMovementX: true,
            lockMovementY: true,
            selectable: true,
            evented: true,
            hasControls: true,
            hasBorders: true,
          });

          obj = new Group([bg, label], {
            interactive: true,
            subTargetCheck: true,
            selectable: true,
            hasControls: false,
            hasBorders: true,
            lockScalingX: true,
            lockScalingY: true,
            lockRotation: true,
            padding: 20,
          });
          break;
        }

        case 'banner-cta': {
          centerOnCanvas = false;
          obj = buildBannerCtaGroup(isMobile ? 'mobile' : 'desktop');
          break;
        }

        case 'image':
          if (imageDataUrl) {
            const sizeInBytes = getDataUrlSizeInBytes(imageDataUrl);
            const maxSize = 5 * 1024 * 1024; // 5MB

            if (sizeInBytes > maxSize) {
              alert({ alertType: 'warning', title: 'A imagem deve ter no máximo 5MB' });
              return;
            }

            void FabricImage.fromURL(imageDataUrl).then(img => {
              const scale = Math.min(
                (canvas.getWidth() - 40) / (img.width ?? 400),
                (canvas.getHeight() - 40) / (img.height ?? 400),
                1,
              );
              img.set({
                scaleX: scale,
                scaleY: scale,
                data: { id: crypto.randomUUID(), label: 'Imagem' },
              });
              canvas.add(img);
              canvas.centerObject(img);
              canvas.setActiveObject(img);
              canvas.requestRenderAll();
              saveCurrentSlide(variantKey);
              pushSnapshot(variantKey);
            });
            return;
          }
          break;
      }

      if (obj) {
        canvas.add(obj);
        if (centerOnCanvas) canvas.centerObject(obj);
        canvas.setActiveObject(obj);
        canvas.requestRenderAll();
        saveCurrentSlide(variantKey);
        pushSnapshot(variantKey);
      }
    },
    [getActiveCanvas, saveCurrentSlide, pushSnapshot, variantKey, activeDevice, alert],
  );

  return { addElement } as const;
}
